/*
  <pt-bundle>  Frequently bought together (sections/pt-bundle.liquid)
  Loads curated add-ons when the section nears the viewport, keeps only the ones that fit the
  same truck, and adds the ticked items to the cart in one request.
*/
(function () {
  if (window.customElements.get('pt-bundle')) return;

  function norm(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function parseRows(raw) {
    if (!raw || raw === 'null') return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  // Vehicle-specific add-ons must share make + model, overlap in years and match the bed when
  // both list one. Items with no fitment rows (or flagged universal) are not vehicle parts.
  function fits(main, cand) {
    if (!cand || cand.u || !cand.r || !cand.r.length) return true;
    if (!main || !main.r || !main.r.length) return false;
    return main.r.some(function (a) {
      return cand.r.some(function (b) {
        return norm(a[2]) === norm(b[2]) &&
          norm(a[3]) === norm(b[3]) &&
          a[0] <= b[1] && b[0] <= a[1] &&
          (!a[4] || !b[4] || norm(a[4]) === norm(b[4]));
      });
    });
  }

  class PtBundle extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      var self = this;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(function (entries) {
          if (entries.some(function (e) { return e.isIntersecting; })) {
            self.observer.disconnect();
            self.load();
          }
        // Generous top margin too: a fast jump to the footer (phones have a tall one) can carry
        // the section past the viewport without ever overlapping it.
        }, { rootMargin: '6000px 0px 800px 0px' });
        // The element itself is hidden, so observe its parent section.
        this.observer.observe(this.parentElement || this);
      } else {
        this.load();
      }
    }

    load() {
      var self = this;
      fetch(this.dataset.url, { headers: { Accept: 'text/html' } })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) { if (html) self.build(html); })
        .catch(function () {});
    }

    build(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var candidates = Array.prototype.slice.call(doc.querySelectorAll('[data-bundle-card]'));
      var mainCard = this.querySelector('.ptb__card--main');
      if (!mainCard) return;
      var mainRows = parseRows(mainCard.getAttribute('data-rows'));
      var max = parseInt(this.dataset.max, 10) || 3;
      var seenTypes = {};
      seenTypes[norm(mainCard.getAttribute('data-type'))] = true;
      var mainId = mainCard.getAttribute('data-product-id');

      var picked = [];
      for (var i = 0; i < candidates.length && picked.length < max; i++) {
        var c = candidates[i];
        if (c.getAttribute('data-available') !== 'true') continue;
        if (c.getAttribute('data-master') === 'true') continue;
        if (c.getAttribute('data-product-id') === mainId) continue;
        var type = norm(c.getAttribute('data-type'));
        if (seenTypes[type]) continue;
        if (!fits(mainRows, parseRows(c.getAttribute('data-rows')))) continue;
        seenTypes[type] = true;
        picked.push(c);
      }
      if (!picked.length) return;

      var list = this.querySelector('[data-bundle-items]');
      picked.forEach(function (card) { list.appendChild(document.importNode(card, true)); });
      this.style.setProperty('--ptb-n', String(picked.length + 1));

      try {
        this.variants = JSON.parse(this.querySelector('[data-bundle-variants]').textContent);
      } catch (e) {
        this.variants = [];
      }

      this.bind();
      this.syncMain();
      this.update();
      this.hidden = false;
      this.fitImages();
      var self = this;
      window.addEventListener('resize', function () {
        window.clearTimeout(self.resizeTimer);
        self.resizeTimer = window.setTimeout(function () { self.fitImages(); }, 150);
      });
    }

    // Tell the browser the real rendered width so it picks a sharp srcset candidate.
    fitImages() {
      this.querySelectorAll('.ptb__img').forEach(function (img) {
        var w = Math.ceil(img.getBoundingClientRect().width);
        if (w > 0) img.sizes = w + 'px';
      });
    }

    bind() {
      var self = this;
      this.addEventListener('change', function (e) {
        var t = e.target;
        if (t.matches('[data-bundle-variant]')) {
          var opt = t.options[t.selectedIndex];
          var card = t.closest('[data-bundle-card]');
          var box = card.querySelector('[data-bundle-check]');
          box.setAttribute('data-variant-id', opt.value);
          box.setAttribute('data-price', opt.getAttribute('data-price'));
          self.paintCardPrice(card, parseInt(opt.getAttribute('data-price'), 10), parseInt(opt.getAttribute('data-compare'), 10));
        }
        self.update();
      });
      this.querySelector('[data-bundle-add]').addEventListener('click', function () { self.add(); });

      // Follow the variant picked in the main product form above.
      document.addEventListener('change', function (e) {
        if (e.target instanceof Element && e.target.closest('form[data-ajax-atc]') && !self.contains(e.target)) {
          window.setTimeout(function () { self.syncMain(); self.update(); }, 60);
        }
      });
    }

    mainForm() {
      return document.querySelector('.main-product form[data-ajax-atc]') || document.querySelector('form[data-ajax-atc]');
    }

    syncMain() {
      var form = this.mainForm();
      var idInput = form && form.querySelector('[name="id"]');
      if (!idInput || !this.variants) return;
      var id = String(idInput.value);
      var variant = this.variants.filter(function (v) { return String(v.id) === id; })[0];
      if (!variant) return;
      var card = this.querySelector('.ptb__card--main');
      var box = card.querySelector('[data-bundle-check]');
      box.setAttribute('data-variant-id', id);
      box.setAttribute('data-price', variant.price);
      this.paintCardPrice(card, variant.price, variant.compare_at_price || 0);
    }

    money(cents) {
      try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100);
      } catch (e) {
        return '$' + (cents / 100).toFixed(2);
      }
    }

    paintCardPrice(card, price, compare) {
      card.querySelector('[data-card-price]').textContent = this.money(price);
      var was = card.querySelector('[data-card-compare]');
      if (was) {
        was.hidden = !(compare > price);
        if (compare > price) was.textContent = this.money(compare);
      }
    }

    checked() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-bundle-check]')).filter(function (b) { return b.checked; });
    }

    update() {
      var self = this;
      var total = 0;
      var lines = '';
      this.querySelectorAll('[data-bundle-check]').forEach(function (box) {
        var card = box.closest('[data-bundle-card]');
        card.classList.toggle('is-off', !box.checked);
        if (!box.checked) return;
        var price = parseInt(box.getAttribute('data-price'), 10) || 0;
        total += price;
        var li = document.createElement('li');
        var name = document.createElement('span');
        name.textContent = box.getAttribute('data-name');
        var amount = document.createElement('span');
        amount.textContent = self.money(price);
        li.appendChild(name);
        li.appendChild(amount);
        lines += li.outerHTML;
      });
      var count = this.checked().length;
      this.querySelector('[data-bundle-total]').textContent = this.money(total);
      this.querySelector('[data-bundle-lines]').innerHTML = lines;
      var label = (this.dataset.button || 'Add [count] items to cart').replace('[count]', count);
      if (count === 1) label = label.replace('items', 'item');
      var btn = this.querySelector('[data-bundle-add]');
      if (!btn.disabled) btn.textContent = label;
      this.label = label;
    }

    add() {
      var self = this;
      var btn = this.querySelector('[data-bundle-add]');
      var msg = this.querySelector('[data-bundle-msg]');
      var form = this.mainForm();
      var mainBox = this.querySelector('.ptb__card--main [data-bundle-check]');
      var mainItem = { id: parseInt(mainBox.getAttribute('data-variant-id'), 10), quantity: 1, properties: {} };

      // Carry the main form's line properties (e.g. the fitment note) and quantity.
      if (form) {
        var fd = new FormData(form);
        fd.forEach(function (value, key) {
          var m = key.match(/^properties\[(.+)\]$/);
          if (m && value !== '') mainItem.properties[m[1]] = value;
        });
        var qty = parseInt(fd.get('quantity'), 10);
        if (qty > 0) mainItem.quantity = qty;
      }

      var items = [mainItem];
      this.checked().forEach(function (box) {
        if (box === mainBox) return;
        items.push({ id: parseInt(box.getAttribute('data-variant-id'), 10), quantity: 1 });
      });

      btn.disabled = true;
      btn.textContent = 'Adding…';
      fetch(this.dataset.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          return r.json().then(function (data) { return r.ok ? data : Promise.reject(data); });
        })
        .then(function () {
          btn.disabled = false;
          btn.textContent = self.label;
          self.afterAdd();
        })
        .catch(function (err) {
          btn.disabled = false;
          btn.textContent = self.label;
          if (msg) msg.textContent = (err && (err.description || err.message)) || 'Could not add these items. Please try again.';
        });
    }

    afterAdd() {
      var cartUrl = this.dataset.cartUrl || '/cart';
      fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.querySelectorAll('[data-cart-count]').forEach(function (el) {
            el.textContent = cart.item_count > 0 ? cart.item_count : '';
            el.classList.toggle('is-visible', cart.item_count > 0);
          });
        })
        .catch(function () {});

      // The theme opens and refreshes its drawer for any click on [data-cart-drawer-open].
      if (document.querySelector('[data-cart-drawer]')) {
        var opener = document.createElement('button');
        opener.type = 'button';
        opener.hidden = true;
        opener.setAttribute('data-cart-drawer-open', '');
        document.body.appendChild(opener);
        opener.click();
        opener.remove();
      } else {
        window.location.href = cartUrl;
      }
    }
  }

  window.customElements.define('pt-bundle', PtBundle);
})();
